import { formatCurrency } from 'common/FormatUtilities';
import { gt } from 'common/PatternMatchingUtilities';
import { formatNumOrdersReferralBanner } from 'common/ReferTextUtilities';
import { Slugs } from 'common/urls/Slugs';
import { useCallback } from 'react';
import { useRouteMatch } from 'react-router';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { PromoCodeType, useGetRemainingGiftCardBalanceBeforeGroupOrderQuery } from 'src/gqlReactTypings.generated.d';
import { useIsImmersiveLandingPage } from 'src/shared/hooks/experiments/useImmersiveLandingPage';
import { useLocation } from 'src/shared/hooks/useLocation';
import { Notifications } from 'src/shared/Notifications';
import { ALL_WAITLIST_ROUTES, Routes } from 'src/shared/Routes';
import { getSlug, locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { match, P } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { CloseButton } from './components/DishFirstHeader/styles/modal';
import { useShowConsumerHeaderPersistentBanner } from './components/header-v2/useShowConsumerHeaderPersistentBanner';
import { useMealPlanSurfacingLogic } from './meal-plans/hooks/useMealPlanSurfacingLogic';
import { getDiscountText, useMealPlanDiscount } from './meal-plans/useMealPlanDiscount';

export const ConsumerBanner: React.FC = () => {
  const isImmersiveLandingPage = useIsImmersiveLandingPage();
  const { enabled, handlePromoClose, promoCode } = useShowConsumerHeaderPersistentBanner();
  const { isSmallScreen } = useScreenContext();
  const { navigateToMealPlanPreferences } = useMealPlanSurfacingLogic();
  const mealPlanGiftCardsEnabled = useFeatureFlag('mealPlanGiftCardsEnabled');

  const location = useLocation();
  const hideBanner = locationInRoutes(location, [
    ...Object.values(ALL_WAITLIST_ROUTES),
    Routes.CONSUMER_ACCOUNT_PROFILE,
    Routes.CONSUMER_ACCOUNT_ORDERS,
    Routes.CONSUMER_ORDER_CONFIRMATION,
    Routes.CONSUMER_ORDERS_CONFIRMATION,
    Routes.CONSUMER_ACCOUNT_MESSAGES,
    Routes.CONSUMER_ACCOUNT_SHARE,
    Routes.CONSUMER_ACCOUNT_SHARE_SHEF,
    Routes.CONSUMER_ACCOUNT_SHARE_FOOD_ITEM,
    Routes.CONSUMER_ACCOUNT_SHARE_ORDER,
    Routes.CONSUMER_ACCOUNT_PAYMENT_METHODS,
    Routes.CONSUMER_ACCOUNT_DELIVERY_ADDRESSES,
    Routes.CONSUMER_ACCOUNT_SUBSCRIPTION,
    Routes.CONSUMER_ACCOUNT_TABLERUN_SUBSCRIPTIONS,
    Routes.CONSUMER_ACCOUNT_NOTIFICATIONS,
    Routes.CONSUMER_FOLLOWING,
    Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER,
    Routes.CONSUMER_DONATE,
    Routes.CONSUMER_DONATE_THANKS,
  ]);
  const { discountText } = useMealPlanDiscount();

  const isOnMealPlanIntake = locationInRoutes(location, [
    Routes.CONSUMER_LANDING,
    Routes.CONSUMER_MEAL_PLANS_CHECKOUT,
    Routes.CONSUMER_MEAL_PLANS,
    Routes.CONSUMER_MEAL_PLANS_PREFERENCES,
  ]);

  const isOnMealPlanEditOrder = locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_EDIT_ORDER]);
  const route = useRouteMatch(Routes.CONSUMER_MEAL_PLANS_EDIT_ORDER);
  const groupOrderId = getSlug(route, Slugs.GROUP_ORDER_ID) ?? '';
  const { data } = useGetRemainingGiftCardBalanceBeforeGroupOrderQuery({
    variables: { groupOrderId },
    skip: !groupOrderId || !isOnMealPlanEditOrder || !mealPlanGiftCardsEnabled,
  });
  const remainingMealPlanGiftCardBalance = data?.getRemainingGiftCardBalanceBeforeGroupOrder?.remainingBalance ?? 0;

  const isOnMarketplaceCheckout = locationInRoutes(location, [Routes.CONSUMER_ORDER_FINALIZE]);
  const isOnHomepage = locationInRoutes(location, [Routes.CONSUMER_LANDING]);

  const onBannerClose = useCallback(handlePromoClose, [handlePromoClose]);

  const valueString =
    getDiscountText(promoCode?.remainingBalance ?? promoCode?.value ?? 0, promoCode?.isPercentage ?? false) ||
    discountText;

  const handleBannerClick = useCallback(() => {
    if (isOnHomepage) {
      navigateToMealPlanPreferences({});
      return;
    }
    Notifications.success(valueString ? `${valueString} off applied` : 'Promo applied', {
      className: 'w-fit self-end',
    });
  }, [isOnHomepage, navigateToMealPlanPreferences, valueString]);

  const valueText = promoCode ? <span className='font-juniper-bay text-h5'>{valueString}</span> : null;

  const orderText = formatNumOrdersReferralBanner(promoCode?.usesRemainingForUser ?? 0);
  const relevantRemainingUsesCopy = valueText ? (
    <span>
      Yay! You get {valueText} off{orderText && ' '}
      {orderText}!
    </span>
  ) : null;

  const bannerMessage = match({
    maxUsesPerUser: promoCode?.maxUsesPerUser,
    firstPromoOnly: promoCode?.firstPromoOnly,
    isFreeDelivery: promoCode?.isFreeDelivery,
    isOnMealPlanIntake,
    promoCode: promoCode?.code,
    promoCodeType: promoCode?.promoCodeType,
    numUsesRemainingForUser: promoCode?.usesRemainingForUser ?? 0,
    isPercentage: promoCode?.isPercentage,
    isMealPlanEnabled: promoCode?.isMealPlanEnabled,
    isOnMealPlanEditOrder,
    isOnMarketplaceCheckout,
    hasRemainingMealPlanGiftCardBalance: remainingMealPlanGiftCardBalance > 0,
    groupOrderId,
    valueText,
  })
    .with({ isOnMealPlanEditOrder: true, groupOrderId: P.not(null), hasRemainingMealPlanGiftCardBalance: true }, () => (
      <span>Gift card balance of {formatCurrency(remainingMealPlanGiftCardBalance)} will be applied</span>
    ))
    .with(
      { isMealPlanEnabled: true, numUsesRemainingForUser: gt(0), maxUsesPerUser: gt(1) },
      () => relevantRemainingUsesCopy
    )
    .with(
      { isMealPlanEnabled: true, isOnMarketplaceCheckout: false, promoCodeType: P.not(PromoCodeType.Referral) },
      () => <span>Claim {valueText} off your Weekly Shef Service!</span>
    )
    .with(
      { isOnMarketplaceCheckout: true, isFreeDelivery: true },
      () => 'Yay! You get free delivery on your next order!'
    )
    .with({ firstPromoOnly: true }, () => <span>You get {valueText} off your first order!</span>)
    .with({ valueText: P.not(P.nullish) }, () => relevantRemainingUsesCopy)
    .otherwise(() => undefined);

  const isMealPlanGiftCardBanner = isOnMealPlanEditOrder && groupOrderId && remainingMealPlanGiftCardBalance > 0;
  const promoBannerHidden = !bannerMessage || !enabled || hideBanner || isImmersiveLandingPage;

  if (promoBannerHidden && !isMealPlanGiftCardBanner) {
    return null;
  }

  return (
    <div
      id='consumer-banner'
      className={
        'z-banner flex w-full items-center justify-between bg-mint-50 p-2 ' +
        'font-sans text-caption-700-caps uppercase text-mint-600'
      }>
      <div className={`flex w-full items-start justify-center gap-1 font-semibold`}>
        <div className='flex flex-col items-start justify-center gap-0 sm:flex-row sm:items-center sm:gap-1'>
          {promoCode?.isMealPlanEnabled ? (
            <span className='cursor-pointer hover:underline' onClick={handleBannerClick}>
              {bannerMessage}
            </span>
          ) : (
            <span className=''>{bannerMessage}</span>
          )}
          {!isSmallScreen && !isMealPlanGiftCardBanner && (
            <span className='mt-1 text-caption-400 italic'>Discount applied at checkout</span>
          )}
        </div>
      </div>
      <div className='cursor-pointer'>
        <CloseButton onClick={onBannerClose} />
      </div>
    </div>
  );
};
